import { graphql } from 'gatsby';
import { ImageElement, RichTextElement } from '@kontent-ai/gatsby-components';
import { useMemo } from 'react';
import { type BlockHTMLId } from '@/utils/renderBlock';
import StarRating from '@/components/StarRating/StarRating';
import { applyPaddingCSS } from '@/utils/generatePadding';
import { breakpoints } from '@/constants/breakpoints';
import useActiveLink from '../Link/hooks/useActiveLink';
import { isRichTextValid } from '../RichTextBlock/utils';
import useResolveRichTextInternalLink from '../RichTextBlock/hooks/useResolveRichTextInternalLink';
import LinkToggle from '@/components/LinkToggle/LinkToggle';
import ScrollDownForMore from '../Forms/components/ScrollDownForMore/ScrollDownForMore';
import ContactForm from '../Forms/ContactForm/ContactForm';
import EditionsContactForm from '../Forms/EditionsContactForm/EditionsContactForm';
import ProductContactForm from '../Forms/ProductContactForm/ProductContactForm';
import LoginSectionForm from '../Forms/LoginSectionForm/LoginSectionForm';
import CloudLoginForm from '../Forms/CloudLoginForm/CloudLoginForm';
import SERPForm from '../SERPForm/SERPForm';
import {
  ScrollDownForMoreWrapper,
  StyledButtonList,
  StyledButtonListItem,
  StyledContainer,
  StyledFormContainer,
  FormToggleWrapper,
  StyledFormHeadline,
  StyledFormSubheadline,
  StyledFormInner,
  StyledFormLead,
  StyledFormLeadHeader,
  StyledFormLeadHeaderMobile,
  StyledFormLogo,
  StyledFormWrap,
  StyledHeadline,
  StyledRichText,
  StyledCompanyLogos,
  StyledCompanyLogoItem,
  StyledKontentImage,
  StyledSection,
  StyledStarRatingContainer,
  StyledLink,
  StyledFormWrapper,
  StyledCampaignForm,
} from './FormContainer.styles';

const DEFAULT_LOGO_WIDTH = 190;
const DEFAULT_LOGO_HEIGHT = 52;

const FormContainer = ({
  system,
  formContainerElements: elements,
  id,
}: Queries.FORM_CONTAINERFragment & BlockHTMLId) => {
  const {
    options,
    headline,
    theme,
    logo,
    ctas,
    star_rating,
    body,
    company_logos,
    scroll_down_for_more,
    scroll_down_for_more_spacing,
    form_toggle,
    form_headline,
    form_subheadline,
    form,
    background_color,
    form_direction,
    form_options,
  } = elements;

  const logoImage = logo?.value
    ? {
        ...logo.value,
        width: logo.value.width ?? DEFAULT_LOGO_WIDTH,
        height: logo.value.height ?? DEFAULT_LOGO_HEIGHT,
        description: logo.value.description ?? logo.value.name,
        image_id: logo.value.name ?? '',
      }
    : null;

  const hasFormHeadline =
    typeof form_headline?.value === 'string' && form_headline.value.length > 0;
  const hasFormSubheadline =
    typeof form_subheadline?.value === 'string' && form_subheadline.value.length > 0;

  const activeLinkId = useActiveLink({ links: ctas?.value ?? [] })?.system.id;
  const CTAs = useMemo(() => {
    return ctas?.value.map((button) => {
      const isActive = button.system.id === activeLinkId;
      return (
        <StyledButtonListItem key={button.system.id}>
          <StyledLink
            {...button}
            active={isActive}
            variant="primary_small"
            aria-selected={isActive}
          />
        </StyledButtonListItem>
      );
    });
  }, [ctas, activeLinkId]);

  const hasCTAs = Array.isArray(CTAs) && CTAs.length > 0;
  const mobileButtonsOnTop = options?.value?.includes('mobile_buttons_on_top') ?? false;
  const showMobileCTAs = hasCTAs && mobileButtonsOnTop;

  const toggleLinks = form_toggle?.value?.formToggleElements.links.value;
  const showToggle = !!toggleLinks && toggleLinks.length === 2;

  const hasHeadline = typeof headline?.value === 'string';
  const shouldAlignFormWithHeader =
    (logoImage && form_options?.value?.includes('align_form_top_with_content_headline')) ?? false;
  const hasStarRating = !!star_rating?.value;
  const mobileHeadlineOnTop = options?.value?.includes('mobile_headline_on_top') ?? false;
  const showMobileHeadline = hasHeadline && mobileHeadlineOnTop;
  const showLogoOnTopOfFormOnMobile = options?.value?.includes('logo_on_top_of_form') ?? false;

  const hasDropShadow = options?.value?.includes('form_drop_shadow') ?? false;

  const hasBody = isRichTextValid(body?.value);
  const has4or8CompanyLogos =
    company_logos?.value?.length === 4 || company_logos?.value?.length === 8;
  const hasScrollDownForMoreButton = !!scroll_down_for_more?.value;

  const hasFormLead = hasCTAs || hasBody || hasScrollDownForMoreButton || has4or8CompanyLogos;
  const formLogo = (
    <StyledFormLogo showOnMobile={showLogoOnTopOfFormOnMobile}>
      {logoImage && <ImageElement objectFit="contain" image={logoImage} />}
    </StyledFormLogo>
  );

  return (
    <StyledSection
      data-codename={system.codename}
      css={applyPaddingCSS(elements)}
      id={id}
      theme={theme.value}
      backgroundColor={background_color?.value ?? 'white'}
    >
      <StyledContainer>
        {showLogoOnTopOfFormOnMobile && logoImage !== null && formLogo}
        {showMobileCTAs && (
          <StyledFormLeadHeaderMobile logoAlignment={headline?.value === ''}>
            <StyledButtonList>{CTAs}</StyledButtonList>
          </StyledFormLeadHeaderMobile>
        )}
        {showMobileHeadline && (
          <StyledFormLeadHeaderMobile logoAlignment={headline?.value === ''}>
            <StyledHeadline>{headline.value}</StyledHeadline>
          </StyledFormLeadHeaderMobile>
        )}
        <StyledFormWrap alignFormWithHeader={shouldAlignFormWithHeader}>
          <StyledFormInner hasDropShadow={hasDropShadow}>
            <StyledFormContainer backgroundColor={background_color?.value ?? 'white'}>
              {hasStarRating && (
                <StyledStarRatingContainer showOnMobile>
                  <StarRating {...star_rating.value} />
                </StyledStarRatingContainer>
              )}
              {showToggle && (
                <FormToggleWrapper>
                  <LinkToggle links={toggleLinks} />
                </FormToggleWrapper>
              )}
              {hasFormHeadline && <StyledFormHeadline>{form_headline.value}</StyledFormHeadline>}
              {hasFormSubheadline && (
                <StyledFormSubheadline>{form_subheadline.value}</StyledFormSubheadline>
              )}
              <StyledFormWrapper>
                {form.value.__typename === 'kontent_item_contact_form' && (
                  <ContactForm {...form.value} />
                )}
                {form.value.__typename === 'kontent_item_editions_contact_form' && (
                  <EditionsContactForm {...form.value} />
                )}
                {form.value.__typename === 'kontent_item_product_contact_form' && (
                  <ProductContactForm {...form.value} />
                )}
                {form.value.__typename === 'kontent_item_login_section_form' && (
                  <LoginSectionForm {...form.value} />
                )}
                {form.value.__typename === 'kontent_item_cloud_login_form' && (
                  <CloudLoginForm {...form.value} />
                )}
                {form.value.__typename === 'kontent_item_serp_form' && <SERPForm {...form.value} />}
                {form.value.__typename === 'kontent_item_campaign_form' && (
                  <StyledCampaignForm {...form.value} />
                )}
              </StyledFormWrapper>
            </StyledFormContainer>
          </StyledFormInner>
        </StyledFormWrap>
        {hasFormLead && (
          <StyledFormLead
            direction={form_direction?.value ?? 'right'}
            logoAlignment={headline?.value === ''}
          >
            {!showLogoOnTopOfFormOnMobile && logoImage !== null && formLogo}
            {hasCTAs && (
              <StyledFormLeadHeader hideOnMobile={mobileButtonsOnTop}>
                <StyledButtonList>{CTAs}</StyledButtonList>
              </StyledFormLeadHeader>
            )}
            {hasHeadline && (
              <StyledFormLeadHeader hideOnMobile={mobileHeadlineOnTop}>
                <StyledHeadline>{headline.value}</StyledHeadline>
              </StyledFormLeadHeader>
            )}
            {hasStarRating && (
              <StyledStarRatingContainer>
                <StarRating {...star_rating.value} />
              </StyledStarRatingContainer>
            )}
            {hasBody && (
              <StyledRichText>
                <RichTextElement
                  value={body.value}
                  links={body.links}
                  resolveLink={useResolveRichTextInternalLink}
                />
              </StyledRichText>
            )}
            {has4or8CompanyLogos && (
              <StyledCompanyLogos>
                {company_logos.value.map((logo, index) => (
                  <StyledCompanyLogoItem key={`${logo!.url}-${index}`}>
                    <StyledKontentImage
                      sizes={[
                        { width: 180, height: 72 },
                        { width: 157, height: 63, breakpoint: breakpoints.TABLET },
                        { width: 121, height: 48, breakpoint: breakpoints.DESKTOP },
                      ]}
                      src={logo!.url}
                      alt={logo!.description ?? 'Company logo'}
                      loading="eager"
                    />
                  </StyledCompanyLogoItem>
                ))}
              </StyledCompanyLogos>
            )}
            {hasScrollDownForMoreButton && (
              <ScrollDownForMoreWrapper spacing={scroll_down_for_more_spacing?.value ?? 'none'}>
                <ScrollDownForMore {...scroll_down_for_more.value} />
              </ScrollDownForMoreWrapper>
            )}
          </StyledFormLead>
        )}
      </StyledContainer>
    </StyledSection>
  );
};

export const fragment = graphql`
  fragment FORM_TOGGLE on kontent_item_form_toggle {
    system {
      codename
    }
    formToggleElements: elements {
      links {
        value {
          system {
            id
          }
          ...LINK
        }
      }
    }
  }

  fragment FORM_CONTAINER on kontent_item_form_container {
    __typename
    system {
      id
      codename
    }
    formContainerElements: elements {
      ...COMPONENT_A_B_TEST
      ...COMPONENT_PERSONALIZATION
      ...PADDING
      headline {
        value
      }
      logo {
        value {
          description
          name
          type
          url
          width
          height
        }
      }
      theme {
        value
      }
      options {
        value
      }
      ctas {
        value {
          system {
            id
          }
          ...LINK
        }
      }
      star_rating {
        value {
          ...STAR_RATING
        }
      }
      body {
        value
        links {
          link_id
          codename
        }
      }
      company_logos {
        value {
          description
          name
          type
          url
          width
          height
        }
      }
      scroll_down_for_more {
        value {
          ...SCROLL_DOWN_FOR_MORE
        }
      }
      scroll_down_for_more_spacing {
        value
      }
      form_toggle {
        value {
          ...FORM_TOGGLE
        }
      }
      form_headline {
        value
      }
      form_subheadline {
        value
      }
      form {
        value {
          ...CAMPAIGN_FORM
          ...CONTACT_FORM
          ...EDITIONS_CONTACT_FORM
          ...PRODUCT_CONTACT_FORM
          ...LOGIN_SECTION_FORM
          ...CLOUD_LOGIN_FORM
          ...SERP_FORM
        }
      }
      background_color {
        value
      }
      form_direction {
        value
      }
      form_options {
        value
      }
    }
  }
`;

export default FormContainer;
